import React, { useEffect, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import * as Flags from "country-flag-icons/string/3x2";
import { useHistory, useLocation } from "react-router-dom";
import MapChart, { VISITED_COUNTRIES } from "./MapChart";
import { getTravelPhotos } from "./travelPhotos";

const flagDataUrl = code =>
  `data:image/svg+xml;utf8,${encodeURIComponent(Flags[code])}`;

const countrySlug = name =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const COUNTRY_BY_SLUG = VISITED_COUNTRIES.reduce((countries, country) => {
  countries[countrySlug(country.name)] = country;
  return countries;
}, {});

const countryFromPath = pathname => {
  const slug = pathname.replace(/^\/|\/$/g, "").toLowerCase();
  return COUNTRY_BY_SLUG[slug] || null;
};

const Map = (props) => {
  const { toggleSwitch } = props;
  const history = useHistory();
  const location = useLocation();
  const initialCountry = useRef(countryFromPath(location.pathname));
  const isInitialCountryDisplay = useRef(Boolean(initialCountry.current));
  const [mapSelection, setMapSelection] = useState(
    initialCountry.current ? initialCountry.current.name : ""
  );
  const [selectedCountry, setSelectedCountry] = useState(initialCountry.current);
  const [activePhoto, setActivePhoto] = useState(0);
  const showcaseRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [content, setContent] = useState({
    content: ""
  });

  const photos = selectedCountry
    ? getTravelPhotos(selectedCountry.code)
    : [];

  useEffect(() => {
    const country = countryFromPath(location.pathname);

    setSelectedCountry(country);
    setMapSelection(country ? country.name : "");
    setContent({ content: "" });
  }, [location.pathname]);

  useEffect(() => {
    setActivePhoto(0);

    if (!selectedCountry) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const behavior = isInitialCountryDisplay.current || reduceMotion
        ? "auto"
        : "smooth";

      showcaseRef.current.scrollIntoView({
        behavior,
        block: "start",
      });
      closeButtonRef.current.focus({ preventScroll: true });
      isInitialCountryDisplay.current = false;
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [selectedCountry]);

  useEffect(() => {
    if (photos.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setActivePhoto(current => (current + 1) % photos.length);
    }, 4500);

    return () => window.clearInterval(timer);
  }, [photos.length, selectedCountry]);

  useEffect(() => {
    const closeOnEscape = event => {
      if (event.key === "Escape" && selectedCountry) {
        setSelectedCountry(null);
        setMapSelection("");
        history.replace("/");
      }
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [history, selectedCountry]);

  const closeShowcase = () => {
    setSelectedCountry(null);
    setMapSelection("");
    history.replace("/");
  };

  const openShowcase = country => {
    setContent({ content: "" });
    setSelectedCountry(country);
    setMapSelection(country.name);
    history.push(`/${countrySlug(country.name)}`);
  };

  const changePhoto = direction => {
    setActivePhoto(current =>
      (current + direction + photos.length) % photos.length
    );
  };

  return (
    <div className={`mapDiv ${toggleSwitch ? "mapDiv--light" : "mapDiv--dark"}`}>
      <div
        ref={showcaseRef}
        className={`mapShowcase ${selectedCountry ? "mapShowcase--open" : ""}`}
      >
        <div className="mapShowcase__map" aria-hidden={Boolean(selectedCountry)}>
          <MapChart
            mapSelection={mapSelection}
            setMapSelection={setMapSelection}
            setTooltipContent={setContent}
            toggleSwitch={toggleSwitch}
            onCountrySelect={openShowcase}
            isInactive={Boolean(selectedCountry)}
          />
        </div>

        <section
          className="countryGallery"
          role="dialog"
          aria-hidden={!selectedCountry}
          aria-labelledby="country-gallery-title"
        >
          {selectedCountry && (
            <>
              <header className="countryGallery__header">
                <h2 id="country-gallery-title">
                  <img
                    src={flagDataUrl(selectedCountry.code)}
                    alt=""
                    aria-hidden="true"
                  />
                  {selectedCountry.name}
                </h2>
                <button
                  ref={closeButtonRef}
                  type="button"
                  className="countryGallery__close"
                  onClick={closeShowcase}
                  aria-label="Return to the map"
                >
                  <kbd>esc</kbd>
                </button>
              </header>

              {photos.length > 0 ? (
                <div className="countryGallery__collection">
                  <div className="countryGallery__hero">
                    <img
                      key={photos[activePhoto].src}
                      src={photos[activePhoto].src}
                      alt={photos[activePhoto].alt}
                    />
                    {photos.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="countryGallery__arrow countryGallery__arrow--previous"
                          onClick={() => changePhoto(-1)}
                          aria-label="Previous photo"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          className="countryGallery__arrow countryGallery__arrow--next"
                          onClick={() => changePhoto(1)}
                          aria-label="Next photo"
                        >
                          ›
                        </button>
                      </>
                    )}
                    <span className="countryGallery__counter">
                      {activePhoto + 1} / {photos.length}
                    </span>
                  </div>

                  {photos.length > 1 && (
                    <div className="countryGallery__thumbnails" aria-label="Choose a photo">
                      {photos.map((photo, index) => (
                        <button
                          type="button"
                          key={photo.src}
                          className={index === activePhoto ? "is-active" : ""}
                          onClick={() => setActivePhoto(index)}
                          aria-label={`Show ${photo.alt}`}
                          aria-pressed={index === activePhoto}
                        >
                          <img src={photo.src} alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="countryGallery__empty">
                  <p>
                    Need to build this still
                    {/* <code>src/cod/imgs/travel/{selectedCountry.code}/</code> */}
                  </p>
                </div>
              )}
            </>
          )}
        </section>
      </div>
      <ReactTooltip>{content.content}</ReactTooltip>
    </div>
  );
};

export default Map;
